/**
 * AdjudicationPanel - Right sidebar for adjudication labeling
 *
 * Two sections:
 *   1. Disagreements (top) — requires Mike's judgment, P/A buttons, amber highlighted
 *   2. Agreed (bottom, expanded by default) — auto-resolved, editable for overrides
 *
 * Blinded mode: when reader1/reader2 values are empty strings for disputed
 * conditions, the panel hides the R1/R2 columns entirely and shows a
 * "DISPUTED" pill instead. Used for FDA blinded adjudication (no direction).
 */

import { useEffect, useRef, useCallback, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import { labelMapAtom, LabelInfo } from "../../atoms/labelingAtoms";
import { userIdAtom } from "../../atoms/authAtoms";
import { useLabelingQueries, useCaseLabelsQuery } from "../../hooks/useLabelingQueries";
import { FaCheck, FaTimes, FaChevronLeft, FaChevronRight, FaFileAlt } from "../../utils/icons";
import { WorkListButton } from "./WorkListButton";
import { InstructionsDialog } from "./InstructionsDialog";

interface AdjudicationPanelProps {
  projectId: string;
  readOnly?: boolean;
  caseId: string;
  studyInstanceUid: string;
  labelsToAssign: string[];
  readerLabels: {
    reader1: Record<string, string>;
    reader2: Record<string, string>;
  };
  disagreements: string[];
  onNext: () => void;
  onPrevious: () => void;
  onNextUnlabeled?: () => void;
  onWorkList?: () => void;
  hasNext: boolean;
  hasPrevious: boolean;
  hasNextUnlabeled?: boolean;
  currentIndex: number;
  totalCases: number;
  /** Worklist's `instructions_key` for authenticated project instructions. */
  instructionsKey?: string | null;
}

function ReaderBadge({ value }: { value: string | undefined }) {
  if (!value) return <span className="text-fg-muted text-[10px]">—</span>;
  return (
    <span
      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
        value === "Present" ? "bg-surface-2 text-fg" : "bg-surface text-fg-muted"
      }`}
    >
      {value === "Present" ? "P" : "A"}
    </span>
  );
}

export function AdjudicationPanel({
  caseId,
  studyInstanceUid,
  projectId,
  labelsToAssign,
  readerLabels,
  disagreements,
  onNext,
  onPrevious,
  onNextUnlabeled,
  onWorkList,
  hasNext,
  hasPrevious,
  hasNextUnlabeled,
  currentIndex,
  totalCases,
  instructionsKey,
  readOnly = false,
}: AdjudicationPanelProps) {
  const [showInstructions, setShowInstructions] = useState(false);
  const [labelMap, setLabelMap] = useAtom(labelMapAtom);
  const currentUserId = useAtomValue(userIdAtom);
  const { saveLabelMutation, deleteLabelMutation, progressQuery } = useLabelingQueries(projectId);
  const loadedCasesRef = useRef<Set<string>>(new Set());
  const autoAgreedRef = useRef<Set<string>>(new Set());
  const [agreedExpanded, setAgreedExpanded] = useState(true);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  // Blinded mode: reader1 and reader2 are both empty for every disputed
  // condition (no direction shown to the adjudicator). Hide R1/R2 columns
  // and show a DISPUTED pill instead.
  const blinded = disagreements.every((c) => !readerLabels.reader1[c] && !readerLabels.reader2[c]);

  const { data: existingLabels } = useCaseLabelsQuery(projectId, caseId);

  // Load existing labels from DB into local state
  useEffect(() => {
    if (existingLabels?.labels && existingLabels.labels.length > 0) {
      if (loadedCasesRef.current.has(caseId)) return;
      loadedCasesRef.current.add(caseId);

      setLabelMap((prev) => {
        const existingCaseLabels = prev[caseId] || {};
        const newLabels: Record<string, LabelInfo> = {};
        for (const label of existingLabels.labels) {
          if (!existingCaseLabels[label.label_key]) {
            newLabels[label.label_key] = {
              value: label.label_value,
              labeled_by: label.labeled_by,
              labeled_at: label.labeled_at,
            };
          }
        }
        if (Object.keys(newLabels).length === 0) return prev;
        return { ...prev, [caseId]: { ...existingCaseLabels, ...newLabels } };
      });
    }
  }, [existingLabels, caseId, setLabelMap]);

  // Show agreed conditions with reader consensus in local state (no DB write until explicit click)
  useEffect(() => {
    if (!existingLabels) return;
    if (autoAgreedRef.current.has(caseId)) return;
    autoAgreedRef.current.add(caseId);

    const alreadyLabeled = new Set(existingLabels.labels?.map((l) => l.label_key) ?? []);
    const now = new Date().toISOString();
    const autoLabels: Record<string, LabelInfo> = {};

    for (const cond of labelsToAssign) {
      if (disagreements.includes(cond)) continue;
      if (alreadyLabeled.has(cond)) continue;
      const value = readerLabels.reader1[cond] as "Present" | "Absent";
      if (!value) continue;
      autoLabels[cond] = { value, labeled_by: currentUserId || "unknown", labeled_at: now };
    }

    if (Object.keys(autoLabels).length === 0) return;

    setLabelMap((prev) => ({ ...prev, [caseId]: { ...prev[caseId], ...autoLabels } }));
  }, [existingLabels, caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const caseLabels = labelMap[caseId] || {};
  const disagreementSet = new Set(disagreements);
  const agreedConditions = labelsToAssign.filter((c) => !disagreementSet.has(c));

  // Completion = all disagreements resolved (agreed conditions are auto-handled)
  const disagreementsResolved = disagreements.filter((c) => caseLabels[c]).length;
  const isCaseComplete = disagreementsResolved === disagreements.length && disagreements.length > 0;

  const handleLabelSelect = useCallback(
    async (labelKey: string, labelValue: "Present" | "Absent") => {
      const existing = caseLabels[labelKey];

      if (existing?.value === labelValue) {
        setLabelMap((prev) => {
          const updated = { ...prev[caseId] };
          delete updated[labelKey];
          return { ...prev, [caseId]: updated };
        });
        try {
          await deleteLabelMutation.mutateAsync({
            project_id: projectId,
            case_id: caseId,
            label_key: labelKey,
          });
        } catch (error) {
          console.error("Failed to delete label:", error);
        }
        return;
      }

      setLabelMap((prev) => ({
        ...prev,
        [caseId]: {
          ...prev[caseId],
          [labelKey]: {
            value: labelValue,
            labeled_by: currentUserId || "unknown",
            labeled_at: new Date().toISOString(),
          },
        },
      }));

      try {
        await saveLabelMutation.mutateAsync({
          project_id: projectId,
          case_id: caseId,
          study_instance_uid: studyInstanceUid,
          label_key: labelKey,
          label_value: labelValue,
          notes: "",
        });
      } catch (error) {
        console.error("Failed to save label:", error);
      }
    },
    [
      caseId,
      caseLabels,
      studyInstanceUid,
      currentUserId,
      setLabelMap,
      saveLabelMutation,
      deleteLabelMutation,
      projectId,
    ],
  );

  // Keyboard targeting: explicit ↑/↓ focus wins; otherwise first unresolved
  // (power-labeling default). Falls back to row 0 when all are resolved.
  const firstUnresolved = disagreements.find((c) => !caseLabels[c]);
  const firstUnresolvedIndex = firstUnresolved ? disagreements.indexOf(firstUnresolved) : -1;
  const effectiveFocusIndex =
    focusedIndex !== null ? focusedIndex : Math.max(0, firstUnresolvedIndex);
  const focusedDisputed = effectiveFocusIndex >= 0 ? disagreements[effectiveFocusIndex] : undefined;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (readOnly) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      const total = disagreements.length;
      if (total === 0) return;

      const isUp = e.key === "ArrowUp" || e.key === "k";
      const isDown = e.key === "ArrowDown" || e.key === "j";
      if (isUp || isDown) {
        e.preventDefault();
        setFocusedIndex((prev) => {
          const start = prev !== null ? prev : Math.max(0, firstUnresolvedIndex);
          const next = isUp ? start - 1 : start + 1;
          return Math.max(0, Math.min(total - 1, next));
        });
        return;
      }

      if (!focusedDisputed) return;
      if (e.key.toLowerCase() === "p") {
        e.preventDefault();
        handleLabelSelect(focusedDisputed, "Present");
      } else if (e.key.toLowerCase() === "a") {
        e.preventDefault();
        handleLabelSelect(focusedDisputed, "Absent");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [focusedDisputed, firstUnresolvedIndex, disagreements, handleLabelSelect, readOnly]);

  // Reset explicit focus on case change so power-labeling resumes.
  useEffect(() => {
    setFocusedIndex(null);
  }, [caseId]);

  const isMutating = saveLabelMutation.isPending || deleteLabelMutation.isPending;

  return (
    <div className="sidebar-premium sidebar-premium-border-l" data-testid="right-sidebar">
      <div className="absolute inset-0 bg-signal-ambient pointer-events-none" />

      {/* Header */}
      <div className="relative px-3 py-2 shrink-0 space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={onPrevious}
              disabled={!hasPrevious}
              className="sidebar-btn w-7 h-7"
              title="Previous (←)"
            >
              <FaChevronLeft className="w-3 h-3 text-fg-muted" />
            </button>
            <div className="text-center">
              <span className="text-lg font-bold tracking-tight text-white">
                {currentIndex + 1}
                <span className="text-fg-muted font-light text-sm">/{totalCases}</span>
              </span>
              <span
                className={`ml-2 text-[10px] ${isCaseComplete ? "text-signal-text-tint" : "text-fg-muted"}`}
              >
                {isCaseComplete ? "✓" : `${disagreementsResolved}/${disagreements.length}`}
              </span>
            </div>
            <button
              onClick={onNext}
              disabled={!hasNext}
              className="sidebar-btn w-7 h-7"
              title="Next (→)"
            >
              <FaChevronRight className="w-3 h-3 text-fg-muted" />
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            {onWorkList && <WorkListButton onClick={onWorkList} />}
            {onNextUnlabeled && (
              <button
                onClick={onNextUnlabeled}
                disabled={!hasNextUnlabeled}
                className="sidebar-btn-primary h-7 px-3 flex items-center justify-center gap-1 text-[10px]"
                title="Next unlabeled (N)"
              >
                <span>Skip</span>
                <FaChevronRight className="w-2.5 h-2.5" />
              </button>
            )}
          </div>
        </div>

        {progressQuery.data && (
          <div className="flex items-center gap-2">
            <div className="sidebar-progress-track flex-1">
              <div
                className="sidebar-progress-fill"
                style={{ width: `${progressQuery.data.completion_percentage}%` }}
              />
            </div>
            <span className="text-[10px] text-fg-muted tabular-nums">
              {progressQuery.data.completion_percentage.toFixed(0)}%
            </span>
          </div>
        )}

        {/* Column headers — hide R1/R2 in blinded mode */}
        <div className="flex items-center gap-2 px-1 pt-0.5">
          <span className="flex-1 text-[9px] text-fg-muted uppercase tracking-wider">
            Condition
          </span>
          {!blinded && (
            <>
              <span className="text-[9px] text-fg-muted uppercase tracking-wider w-6 text-center">
                R1
              </span>
              <span className="text-[9px] text-fg-muted uppercase tracking-wider w-6 text-center">
                R2
              </span>
            </>
          )}
          <span className="text-[9px] text-fg-muted uppercase tracking-wider w-16 text-center">
            Adjudicate
          </span>
        </div>
      </div>

      {instructionsKey && (
        <>
          <div className="px-3 pb-1 shrink-0">
            <button
              onClick={() => setShowInstructions(true)}
              className="w-full sidebar-btn flex items-center justify-center gap-1.5 h-7 text-[10px] text-fg-muted hover:text-white"
            >
              <FaFileAlt className="w-3 h-3" />
              <span>Instructions</span>
            </button>
          </div>
          <InstructionsDialog
            projectId={projectId}
            instructionsKey={instructionsKey}
            open={showInstructions}
            onClose={() => setShowInstructions(false)}
          />
        </>
      )}

      {saveLabelMutation.isPending && (
        <div className="px-5 py-2.5 text-xs text-signal-text-tint text-center animate-pulse bg-primary/5 border-y border-primary/10">
          Saving...
        </div>
      )}

      <div className="relative flex-1 overflow-y-auto px-3 py-2 min-h-0">
        {/* Disagreements — requires judgment */}
        <div className="space-y-1.5">
          {disagreements.map((condition, idx) => {
            const myLabel = caseLabels[condition];
            const selectedValue = myLabel?.value;
            const r1 = readerLabels.reader1[condition];
            const r2 = readerLabels.reader2[condition];
            const isFocused = idx === effectiveFocusIndex;

            return (
              <div
                key={condition}
                onClick={() => setFocusedIndex(idx)}
                className={`sidebar-card rounded-lg px-3 py-2 border cursor-pointer ${
                  isFocused
                    ? "border-signal-stroke bg-surface-2 ring-1 ring-signal-ring"
                    : "border-border bg-surface"
                } ${selectedValue ? "opacity-60" : ""}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="flex-1 text-xs font-medium leading-tight text-white/90 min-w-0 truncate">
                    {condition}
                  </span>
                  {blinded ? (
                    <span className="text-[8px] tracking-wider font-semibold uppercase px-1.5 py-0.5 rounded bg-surface-2 text-fg border border-border-strong">
                      Disputed
                    </span>
                  ) : (
                    <>
                      <ReaderBadge value={r1} />
                      <ReaderBadge value={r2} />
                    </>
                  )}
                </div>
                <div className="flex gap-1.5 justify-end">
                  <button
                    onClick={() => handleLabelSelect(condition, "Present")}
                    disabled={isMutating}
                    className={`w-7 h-7 rounded-lg text-xs font-semibold transition-all duration-150 flex items-center justify-center ${
                      selectedValue === "Present"
                        ? "bg-signal text-white shadow-xs shadow-signal/30"
                        : "sidebar-btn hover:border-border-strong"
                    } disabled:opacity-30 disabled:cursor-not-allowed`}
                    title="Present"
                  >
                    <FaCheck className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => handleLabelSelect(condition, "Absent")}
                    disabled={isMutating}
                    className={`w-7 h-7 rounded-lg text-xs font-semibold transition-all duration-150 flex items-center justify-center ${
                      selectedValue === "Absent"
                        ? "bg-signal text-white shadow-xs shadow-signal/30"
                        : "sidebar-btn hover:border-border-strong"
                    } disabled:opacity-30 disabled:cursor-not-allowed`}
                    title="Absent"
                  >
                    <FaTimes className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {isCaseComplete && (hasNextUnlabeled || hasNext) && (
          <div className="mt-3 flex justify-center">
            <div className="sidebar-card flex items-center gap-2 text-[10px] sidebar-text-muted px-3 py-1.5 rounded-full">
              <div className="w-1.5 h-1.5 bg-signal rounded-full" />
              <span>Complete — → or N to continue</span>
            </div>
          </div>
        )}

        {/* Agreed conditions — collapsed but editable for overrides */}
        {agreedConditions.length > 0 && (
          <div className="mt-4">
            <button
              onClick={() => setAgreedExpanded((v) => !v)}
              className="w-full flex items-center gap-2 px-1 py-1 text-[10px] text-fg-muted hover:text-fg-muted transition-colors"
            >
              <span
                className={`transition-transform duration-150 ${agreedExpanded ? "rotate-90" : ""}`}
              >
                ▶
              </span>
              <span>{agreedConditions.length} agreed</span>
              <div className="flex-1 h-px bg-border" />
            </button>

            {agreedExpanded && (
              <div className="mt-1.5 space-y-1.5">
                {agreedConditions.map((condition) => {
                  const myLabel = caseLabels[condition];
                  const selectedValue = myLabel?.value;
                  const agreedValue = readerLabels.reader1[condition];
                  const isOverridden = selectedValue && selectedValue !== agreedValue;

                  return (
                    <div
                      key={condition}
                      className={`sidebar-card rounded-lg px-3 py-2 ${
                        isOverridden ? "border border-border-strong bg-surface-2" : "bg-surface"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="flex-1 text-[11px] font-medium leading-tight text-fg-muted min-w-0 truncate">
                          {condition}
                        </span>
                        <ReaderBadge value={agreedValue} />
                      </div>
                      <div className="flex gap-1.5 justify-end">
                        <button
                          onClick={() => handleLabelSelect(condition, "Present")}
                          disabled={isMutating}
                          className={`w-7 h-7 rounded-lg text-xs font-semibold transition-all duration-150 flex items-center justify-center ${
                            selectedValue === "Present"
                              ? "bg-signal text-white shadow-xs shadow-signal/30"
                              : "sidebar-btn hover:border-border-strong"
                          } disabled:opacity-30 disabled:cursor-not-allowed`}
                          title="Present"
                        >
                          <FaCheck className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleLabelSelect(condition, "Absent")}
                          disabled={isMutating}
                          className={`w-7 h-7 rounded-lg text-xs font-semibold transition-all duration-150 flex items-center justify-center ${
                            selectedValue === "Absent"
                              ? "bg-signal text-white shadow-xs shadow-signal/30"
                              : "sidebar-btn hover:border-border-strong"
                          } disabled:opacity-30 disabled:cursor-not-allowed`}
                          title="Absent"
                        >
                          <FaTimes className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
